/**
 * Deterministic proposals (A7Y-3872, D6-D9): four rule-generated suggestions
 * (roster stage change, roster close, watch item, ticket state mismatch)
 * that an Approve/Reject decision turns into an accepted effect on the next
 * run. Every open item names the target and links its layer-1 evidence, so
 * the click is never blind ("HELM's proposal, not applied" — code proposes,
 * Yevhen decides).
 *
 * A stale approval or rejection — the target moved since the proposal's
 * revision (D8) — is disclosed as Refused with the date it was decided,
 * never silently dropped. Three distinct cases land in Refused (Karen round
 * 1 F6), and the copy must not conflate them: a carried proposal that went
 * stale with no decision at all ever recorded against it (`refused` is
 * null); a decided-then-superseded approve (`refused.kind === 'approve'`);
 * and a decided-then-superseded reject (`refused.kind === 'reject'`). Only
 * the second case is honestly described as "you approved" — the other two
 * never happened, and saying so would misattribute a decision Yevhen didn't
 * make. Undo on an accepted proposal is today-only (`useHelmReadOnly`,
 * D15): a past day is a record of what stood then.
 *
 * An approved proposal says what became of it in the roster (A7Y-4733): the
 * next run's write-back records a `HelmWritebackDoc` per approved action,
 * which `writebacks` carries here — written (with its commit), not written
 * (with why), failed (retried at the next run), or not reached yet. Undo goes
 * once the write-back is applied: it would revoke the action but leave the
 * roster commit in place, so the page would imply a reversal that never happens.
 */
import { Badge, Button, Table, TableBody, TableCell, TableRow } from '@a7y/tailwind-catalyst';
import type {
  HelmActionInput,
  HelmFacts,
  HelmProposal,
  HelmWritebackDoc,
  HelmWritebackReason,
} from '@a7y/platform-health/helm';
import { buildHelmLinkIndex, firstLinkForKeys } from '../links';
import { useHelmReadOnly } from '../readOnly';
import { isRefusedProposal, isStaleCloseProposal } from '../staleRows';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';
import { HelmLink } from '../HelmLink';

/** `roster_close` → `roster close`, shown in a `Badge` (`capitalize` handles
 * the leading letter) — the proposal's own `kind` field, mechanically
 * de-slugged rather than mapped through an invented label per kind. */
function kindLabel(kind: HelmProposal['kind']): string {
  return kind.replace(/_/g, ' ');
}

/** The Refused-list copy for one proposal (Karen round 1 F6). A `refused`
 * decision always names what was decided and when; a `null` one means it
 * simply went stale carried forward with no decision ever recorded against
 * it, and must not be worded as if Yevhen approved something. */
function refusedCopy(p: HelmProposal): string {
  if (p.refused == null) {
    return 'the target changed since HELM proposed this; it no longer applies.';
  }
  const date = new Date(p.refused.at).toLocaleDateString();
  if (p.refused.kind === 'reject') {
    return `you rejected an earlier version on ${date}; the target changed since, so that decision no longer applies.`;
  }
  return `you approved an earlier version on ${date}; the target changed since, so it was not applied.`;
}

const ROSTER_REPO_COMMITS = 'https://github.com/ApptonomyInc/a7y-ai-dev/commit';

const NOT_WRITTEN_REASON: Record<HelmWritebackReason, string> = {
  target_not_found: 'the roster row was not found',
  no_roster_effect: 'this proposal changes nothing in the roster',
  push_failed: 'the roster record could not be written',
  parse_failed: 'the roster file could not be read',
};

/** What became of an approved proposal in the roster. `null` when there is
 * nothing to say: a past day with no write-back recorded is a record of what
 * stood then, not a promise about the next run. */
function WritebackState({
  writeback,
  readOnly,
}: {
  writeback: HelmWritebackDoc | undefined;
  readOnly: boolean;
}) {
  if (!writeback) {
    if (readOnly) return null;
    return (
      <span data-testid="helm-proposal-writeback" className="block text-xs text-zinc-500">
        will be written to the roster at the next run — Refresh runs it now
      </span>
    );
  }
  if (writeback.status === 'applied') {
    const sha = writeback.commitSha;
    const linkable = sha != null && /^[0-9a-f]{7,40}$/.test(sha);
    return (
      <span data-testid="helm-proposal-writeback" className="block text-xs text-zinc-500">
        written to the roster
        {sha && (
          <>
            {' · '}
            {linkable ? (
              <a
                href={`${ROSTER_REPO_COMMITS}/${sha}`}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {sha.slice(0, 7)}
              </a>
            ) : (
              sha.slice(0, 7)
            )}
          </>
        )}
      </span>
    );
  }
  if (writeback.status === 'skipped') {
    return (
      <span data-testid="helm-proposal-writeback" className="block text-xs text-zinc-500">
        not written:{' '}
        {writeback.reason ? NOT_WRITTEN_REASON[writeback.reason] : 'no reason recorded'}
      </span>
    );
  }
  return (
    <span
      data-testid="helm-proposal-writeback"
      className="block text-xs text-amber-600 dark:text-amber-400"
    >
      write failed — retried at the next run
    </span>
  );
}

function ProposalTarget({ proposal }: { proposal: HelmProposal }) {
  return proposal.target.url ? (
    <HelmLink href={proposal.target.url} className="font-medium hover:underline">
      {proposal.target.label}
    </HelmLink>
  ) : (
    <span className="font-medium">{proposal.target.label}</span>
  );
}

export function Proposals({
  proposals,
  facts,
  writebacks,
  staleKeys,
  onAction,
  onUndo,
}: {
  proposals: HelmProposal[];
  facts: HelmFacts;
  writebacks: HelmWritebackDoc[];
  /** Stale rows the page renders: an open close proposal citing one is decided
   * on that Stale row (`OverdueStale`), so it is not listed under Open too. */
  staleKeys: ReadonlySet<string>;
  onAction: (input: HelmActionInput) => void;
  onUndo: (actionId: string) => void;
}) {
  const readOnly = useHelmReadOnly();
  const linkIndex = buildHelmLinkIndex(facts);
  const writebackByAction = new Map(writebacks.map((w) => [w.actionId, w] as const));
  const basisUrlFor = (p: HelmProposal) => firstLinkForKeys(linkIndex, p.basis);

  const open = proposals.filter(
    (p) => p.status === 'open' && p.decision == null && !isStaleCloseProposal(p, staleKeys)
  );
  const accepted = proposals.filter(
    (p) => p.decision?.kind === 'approve' || p.status === 'accepted'
  );
  const refused = proposals.filter(isRefusedProposal);
  const rejected = proposals.filter(
    (p) => p.status === 'rejected' && !accepted.includes(p) && !refused.includes(p)
  );

  if (proposals.length === 0) {
    return (
      <HelmSection title="Proposals" lineOn="none">
        <HelmEmpty>none</HelmEmpty>
      </HelmSection>
    );
  }

  return (
    <HelmSection title="Proposals" count={open.length} lineOn="none">
      <div className="space-y-6 text-sm">
        <div>
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
            Open
          </span>
          {open.length === 0 ? (
            <HelmEmpty>none</HelmEmpty>
          ) : (
            <Table dense className={HELM_TABLE_GUTTER}>
              <TableBody>
                {open.map((p) => (
                  <TableRow key={p.key} data-testid="helm-proposal-item">
                    <TableCell className="min-w-0 break-words align-top">
                      <span className="mb-1 flex flex-wrap items-center gap-2">
                        <Badge color="zinc" className="capitalize">
                          {kindLabel(p.kind)}
                        </Badge>
                        <span className="text-xs uppercase text-zinc-500">
                          HELM&apos;s proposal, not applied
                        </span>
                      </span>
                      <ProposalTarget proposal={p} /> — {p.proposed}
                      {basisUrlFor(p) && (
                        <>
                          {' '}
                          ·{' '}
                          <HelmLink href={basisUrlFor(p) ?? ''} className="hover:underline">
                            evidence
                          </HelmLink>
                        </>
                      )}
                    </TableCell>
                    {!readOnly && (
                      <TableCell className="align-top">
                        <span className="flex shrink-0 items-center gap-1.5">
                          <Button
                            outline
                            data-testid="helm-proposal-approve"
                            onClick={() => onAction({ itemKey: p.key, kind: 'approve' })}
                          >
                            Approve
                          </Button>
                          <Button
                            outline
                            data-testid="helm-proposal-reject"
                            onClick={() => onAction({ itemKey: p.key, kind: 'reject' })}
                          >
                            Reject
                          </Button>
                        </span>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div>
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
            Accepted
          </span>
          {accepted.length === 0 ? (
            <HelmEmpty>none</HelmEmpty>
          ) : (
            <Table dense className={HELM_TABLE_GUTTER}>
              <TableBody>
                {accepted.map((p) => (
                  <TableRow key={p.key} data-testid="helm-proposal-accepted">
                    <TableCell className="min-w-0 break-words align-top">
                      <Badge color="zinc" className="mr-2 capitalize">
                        {kindLabel(p.kind)}
                      </Badge>
                      <ProposalTarget proposal={p} /> — {p.proposed}
                      {p.decision && (
                        <span className="ml-2 text-xs text-zinc-500">
                          · {p.decision.actor.name || p.decision.actor.email} ·{' '}
                          {new Date(p.decision.at).toLocaleString()}
                        </span>
                      )}
                      {p.decision && (
                        <WritebackState
                          writeback={writebackByAction.get(p.decision.actionId)}
                          readOnly={readOnly}
                        />
                      )}
                    </TableCell>
                    {!readOnly &&
                      p.decision &&
                      writebackByAction.get(p.decision.actionId)?.status !== 'applied' && (
                        <TableCell className="align-top">
                          <Button
                            outline
                            data-testid="helm-proposal-undo"
                            onClick={() => onUndo(p.decision!.actionId)}
                          >
                            Undo
                          </Button>
                        </TableCell>
                      )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div>
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
            Refused
          </span>
          {refused.length === 0 ? (
            <HelmEmpty>none</HelmEmpty>
          ) : (
            <Table dense className={HELM_TABLE_GUTTER}>
              <TableBody>
                {refused.map((p) => (
                  <TableRow key={p.key} data-testid="helm-proposal-refused">
                    <TableCell className="min-w-0 break-words">
                      <ProposalTarget proposal={p} /> — {refusedCopy(p)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div>
          <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
            Rejected
          </span>{' '}
          <span data-testid="helm-proposal-rejected-count">{rejected.length}</span>
        </div>
      </div>
    </HelmSection>
  );
}
